import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import { useOrgPreferences } from '@/utils/hooks/orgs'
import { OrgPreference } from '@/api/orgsAPI'
import { localisedDate } from '@/utils/dateUtils'

interface OrgMatchPreferencesProps {
  orgUuid: string
  token: string
  needId?: number
  compact?: boolean
}

export default function OrgMatchPreferences({
  orgUuid,
  token,
  needId,
  compact,
}: OrgMatchPreferencesProps) {
  const {
    data: preferences,
    isLoading,
    isError,
    addPreference,
    updatePreference,
    deletePreference,
  } = useOrgPreferences(orgUuid, token, needId)

  const [newPreferenceText, setNewPreferenceText] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  const handleAdd = () => {
    const trimmed = newPreferenceText.trim()
    if (!trimmed) return
    addPreference.mutate(trimmed, {
      onSuccess: () => setNewPreferenceText(''),
    })
  }

  const handleEditStart = (pref: OrgPreference) => {
    setEditingId(pref.id)
    setEditText(pref.preference_text)
  }

  const handleEditSave = () => {
    if (editingId === null) return
    const trimmed = editText.trim()
    if (!trimmed) return
    updatePreference.mutate(
      { preferenceId: editingId, preferenceText: trimmed },
      { onSuccess: () => setEditingId(null) },
    )
  }

  const handleEditCancel = () => {
    setEditingId(null)
    setEditText('')
  }

  const handleDeleteConfirm = () => {
    if (deleteConfirmId === null) return
    deletePreference.mutate(deleteConfirmId, {
      onSuccess: () => setDeleteConfirmId(null),
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#1C3045' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load match preferences. Please try again later.
      </Alert>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        ...(!needId && !compact && { pl: 2 }),
      }}
    >
      {!compact && (
        <Box>
          <Typography variant="body1">
            Use match preferences to refine the types of funding opportunities
            suggested to you.
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {needId
              ? 'These preferences only apply to this funding request.'
              : 'These apply to all of your funding requests.'}
          </Typography>
          {!needId && (
            <Typography variant="body2" color="text.secondary">
              You can also set preferences specific to individual funding
              requests on the{' '}
              <Link component={RouterLink} to="/funding-requests">
                Funding Requests
              </Link>{' '}
              page.
            </Typography>
          )}
        </Box>
      )}

      {!compact && (
        <>
          {/* Add preference form */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              value={newPreferenceText}
              onChange={(e) => setNewPreferenceText(e.target.value)}
              placeholder="e.g. Exclude opportunities that require matched funding"
              size="small"
              fullWidth
              multiline
              maxRows={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <Button
              variant="contained"
              disableRipple
              disabled={!newPreferenceText.trim() || addPreference.isPending}
              onClick={handleAdd}
              sx={{
                bgcolor: '#1C3045',
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                minWidth: 'fit-content',
                boxShadow: 'none',
              }}
            >
              {addPreference.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Add'
              )}
            </Button>
          </Box>

          {addPreference.isError && (
            <Alert severity="error">
              Failed to add preference. Please try again.
            </Alert>
          )}
        </>
      )}

      {/* Preferences list */}
      {preferences && preferences.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {preferences.map((pref) => (
            <Box
              key={pref.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {editingId === pref.id ? (
                <>
                  <TextField
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleEditSave()
                      }
                      if (e.key === 'Escape') {
                        handleEditCancel()
                      }
                    }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleEditSave}
                    disableRipple
                    disabled={!editText.trim() || updatePreference.isPending}
                    sx={{
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#1C3045',
                        bgcolor: 'rgba(28, 48, 69, 0.04)',
                      },
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    {updatePreference.isPending ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress
                          size={20}
                          sx={{ color: 'white', mr: 1 }}
                        />
                        <span>Saving...</span>
                      </Box>
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <IconButton
                    disableRipple
                    size="small"
                    onClick={handleEditCancel}
                    aria-label="Cancel edit"
                    sx={{
                      '&:hover': {
                        borderColor: '#1C3045',
                        bgcolor: 'rgba(28, 48, 69, 0.04)',
                      },
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">
                      {pref.preference_text}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated: {localisedDate(new Date(pref.updated_at))}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditStart(pref)}
                    aria-label="Edit preference"
                    sx={{
                      '&:hover': {
                        borderColor: '#1C3045',
                        bgcolor: 'rgba(28, 48, 69, 0.04)',
                      },
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirmId(pref.id)}
                    aria-label="Delete preference"
                    sx={{
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#1C3045',
                        bgcolor: 'rgba(28, 48, 69, 0.04)',
                      },
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No match preferences set yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Add a preference above to help guide your match results.
          </Typography>
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Preference</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this preference? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            disableRipple
            sx={{
              textTransform: 'none',
              '&:hover': {
                borderColor: '#1C3045',
                bgcolor: 'rgba(28, 48, 69, 0.04)',
              },
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disableRipple
            disabled={deletePreference.isPending}
            sx={{
              textTransform: 'none',
              '&:hover': {
                borderColor: '#1C3045',
                bgcolor: 'rgba(28, 48, 69, 0.04)',
              },
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            {deletePreference.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
